/// <amd-module name="TFS/Admin/Groups/Pivots/ServicePrincipals/Redux/ServicePrincipalsPivotSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { IPermissionPage, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphServicePrincipal, ServicePrincipalsOperation } from "TFS/Admin/Groups/Common/Contracts";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getProjectName, getUrlForGraphSubject } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { ServicePrincipalsPivotActions, ServicePrincipalsPivotActionTypes } from "TFS/Admin/Groups/Pivots/ServicePrincipals/Redux/ServicePrincipalsPivotActions";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { GraphSubject, GraphSubjectQuery, PagedGraphServicePrincipals } from "VSS/Platform/Contracts/Graph";
import { IVssLocationService } from "VSS/Platform/Location";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Root work item saga.
 */
export function* servicePrincipalsPivotSaga(): SagaIterator {
    // Register the initialize Serviceprincipals handler
    yield effects.takeEvery(ServicePrincipalsPivotActionTypes.InitView, initializeServicePrincipalsPivot);
    yield effects.takeEvery(ServicePrincipalsPivotActionTypes.OnRenderMissingItem, fetchMoreServicePrincipals);
    yield effects.takeLatest(ServicePrincipalsPivotActionTypes.SearchServicePrincipals, searchServicePrincipals);
}

export function* initializeServicePrincipalsPivot(action: ActionsOfType<ServicePrincipalsPivotActions, ServicePrincipalsPivotActionTypes.InitView>): SagaIterator {
    const { scopeDescriptor, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.ServicePrincipalsPivot, "Initialize service principals pivot");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    try {
        const pagedServicePrincipals: PagedGraphServicePrincipals = (yield effects.call([graphClient, graphClient.listServicePrincipals], undefined, undefined, scopeDescriptor)) as any;
        let servicePrincipals: CustomGraphServicePrincipal[] = [];

        if (pagedServicePrincipals) {
            const projectName =
                permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

            yield effects.all(
                pagedServicePrincipals.graphServicePrincipals.map(pagedServicePrincipals =>
                    effects.call(function*() {
                        const url = (yield effects.call(
                            getUrlForGraphSubject,
                            pagedServicePrincipals.descriptor,
                            permissionPage,
                            locationService,
                            telemetryLogger,
                            projectName
                        )) as any;
                        servicePrincipals.push({ ...(pagedServicePrincipals as CustomGraphServicePrincipal), linkProps: { href: url } });
                    })
                )
            );

            yield effects.put(ServicePrincipalsPivotActions.initialServicePrincipalsLoadSucceeded(servicePrincipals, pagedServicePrincipals.continuationToken));
        } else {
            yield effects.put(ServicePrincipalsPivotActions.servicePrincipalOperationStatus(ServicePrincipalsOperation.Fetch, Resources.FetchServicePrincipalsFailed, MessageCardSeverity.Error));
            yield effects.put(ServicePrincipalsPivotActions.servicePrincipalsLoadFailed());
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(ServicePrincipalsPivotActions.servicePrincipalOperationStatus(ServicePrincipalsOperation.Fetch, Resources.FetchServicePrincipalsFailed, MessageCardSeverity.Error));
        yield effects.put(ServicePrincipalsPivotActions.servicePrincipalsLoadFailed());
    }
}

export function* fetchMoreServicePrincipals(action: ActionsOfType<ServicePrincipalsPivotActions, ServicePrincipalsPivotActionTypes.OnRenderMissingItem>): SagaIterator {
    const { continuationToken, scopeDescriptor, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.ServicePrincipalsPivot, "Fetch more service principals");
    yield effects.call(telemetryLogger.captureAttemptStart);
    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    if (continuationToken && continuationToken.length) {
        try {
            let pagedServicePrincipals: PagedGraphServicePrincipals = (yield effects.call(
                [graphClient, graphClient.listServicePrincipals],
                continuationToken.toString(),
                scopeDescriptor
            )) as any;
            let servicePrincipals: CustomGraphServicePrincipal[] = [];

            if (pagedServicePrincipals) {
                const projectName =
                    permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

                yield effects.all(
                    pagedServicePrincipals.graphServicePrincipals.map(pagedServicePrincipals =>
                        effects.call(function*() {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                pagedServicePrincipals.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName
                            )) as any;
                            servicePrincipals.push({ ...(pagedServicePrincipals as CustomGraphServicePrincipal), linkProps: { href: url } });
                        })
                    )
                );

                yield effects.put(ServicePrincipalsPivotActions.fetchMoreServicePrincipalsSucceeded(servicePrincipals));
            } else {
                yield effects.put(ServicePrincipalsPivotActions.servicePrincipalsLoadFailed());
                yield effects.put(ServicePrincipalsPivotActions.servicePrincipalOperationStatus(ServicePrincipalsOperation.Fetch, Resources.FetchServicePrincipalsFailed, MessageCardSeverity.Error));
            }
        } catch (error) {
            yield effects.call(telemetryLogger.captureError, error.message || error);
            yield effects.put(ServicePrincipalsPivotActions.servicePrincipalOperationStatus(ServicePrincipalsOperation.Fetch, Resources.FetchServicePrincipalsFailed, MessageCardSeverity.Error));
            yield effects.put(ServicePrincipalsPivotActions.servicePrincipalsLoadFailed());
        }
    } else {
        yield effects.put(ServicePrincipalsPivotActions.servicePrincipalOperationStatus(ServicePrincipalsOperation.Fetch, Resources.FetchServicePrincipalsFailed, MessageCardSeverity.Error));
    }
}

export function* searchServicePrincipals(action: ActionsOfType<ServicePrincipalsPivotActions, ServicePrincipalsPivotActionTypes.SearchServicePrincipals>): SagaIterator {
    const { newKeyword, oldKeyword, scopeDescriptor, isExpandSearchToOrg, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.ServicePrincipalsPivot, "Search ServicePrincipals");
    yield effects.call(telemetryLogger.captureAttemptStart);

    if (!newKeyword) {
        // Reset view
        yield effects.put(ServicePrincipalsPivotActions.updateSearchStatus(SearchStatus.None));
    } else if (oldKeyword !== undefined && localeIgnoreCaseComparer(newKeyword, oldKeyword) !== 0) {
        // Search

        try {
            const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
            const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

            const graphSubjectQuery: GraphSubjectQuery = { query: newKeyword, scopeDescriptor: scopeDescriptor, subjectKind: ["ServicePrincipal"] };

            // Search
            const graphSubjects: GraphSubject[] = (yield effects.call([graphClient, graphClient.querySubjects], graphSubjectQuery)) as any;

            const resultServicePrincipals: CustomGraphServicePrincipal[] = [];

            if (graphSubjects && graphSubjects.length > 0) {
                const projectName =
                    permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

                yield effects.all(
                    graphSubjects.map(graphSubject =>
                        effects.call(function*() {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                graphSubject.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName
                            )) as any;
                            resultServicePrincipals.push({ ...(graphSubject as CustomGraphServicePrincipal), linkProps: { href: url } });
                        })
                    )
                );
            }

            yield effects.put(ServicePrincipalsPivotActions.updateSearchResult(newKeyword, resultServicePrincipals, false));
        } catch (error) {
            yield effects.put(ServicePrincipalsPivotActions.updateSearchStatus(SearchStatus.None)); // Reset view

            yield effects.call(telemetryLogger.captureError, error.message || error);
        }
    } else {
        // Search did not change
        yield effects.put(ServicePrincipalsPivotActions.updateSearchStatus(SearchStatus.Completed));
    }
}
